<template>
  <div class="payment-create main-content">
    <form action="" @submit.prevent="submitPaymentData">
      <div class="page-header">
        <h3 class="page-title">{{ isEdit ? $t('payments.edit_payment') : $t('payments.new_payment') }}</h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link v-if="userType=='supplier'" slot="item-title" to="/supplier/dashboard">{{ $t('general.home') }}</router-link>
            <router-link v-else-if="userType=='anchor'" slot="item-title" to="/anchor/dashboard">{{ $t('general.home') }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link v-if="userType=='supplier'" slot="item-title" to="/supplier/payments">{{ $tc('payments.payment', 2) }}</router-link>
            <router-link v-else-if="userType=='anchor'" slot="item-title" to="/anchor/payments">{{ $tc('payments.payment', 2) }}</router-link>
          </li>
          <li class="breadcrumb-item">{{ isEdit ? $t('payments.edit_payment') : $t('payments.new_payment') }}</li>
        </ol>
        <div class="page-actions row">
          <div class="col-xs-2 mr-4">
            <base-button
              v-if="formData.request_level =='Eligible' && invoiceStatus=='SENT'"
              :outline="true"
              size="large"
              icon="check"
              color="theme"
              @click="saveNonFinanceInvoiceInfo()"
            >
              {{ $t('general.save') }}
            </base-button>
          </div>
          <div class="col-xs-2">
            <base-button
              :loading="isLoading"
              :disabled="isLoading"
              size="large"
              icon="save"
              color="theme"
              type="submit"
            >
              {{ isEdit ? $t('payments.update_payment') : $t('payments.register_payment') }}
            </base-button>
          </div>
        </div>
      </div>
      <div v-if="formData.request_level =='Eligible' && invoiceStatus=='SENT'" class="payment-card card">
        <div class="card-body">
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.schedule_payment_date') }} <span class="text-danger"> *</span> </label>
                <base-date-picker
                  v-model="nonFinanceInvoiceInfo.schedule_payment_date"
                  :calendar-button="true"
                  calendar-button-icon="calendar"
                  @change="$v.nonFinanceInvoiceInfo.schedule_payment_date.$touch()"
                />
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.payment_code') }} </label>
                <base-input
                  :invalid="$v.nonFinanceInvoiceInfo.payment_code.$error"
                  v-model="nonFinanceInvoiceInfo.payment_code"
                  type="text"
                  @input="$v.nonFinanceInvoiceInfo.payment_code.$touch()"
                />
                <div v-if="$v.nonFinanceInvoiceInfo.payment_code.$error">
                  <span v-if="!$v.nonFinanceInvoiceInfo.payment_code.numeric" class="text-danger">
                    {{ $tc('validation.numeric') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('general.withholding_tax_rate') }}</label>
                <div class="base-input">
                  <money
                    :class="{'invalid' : $v.nonFinanceInvoiceInfo.income_tax_withholding.$error}"
                    v-model="income_tax_withholding"
                    v-bind="defaultInputTax"
                    class="input-field"
                  />
                </div>
                <div v-if="$v.nonFinanceInvoiceInfo.income_tax_withholding.$error">
                  <span v-if="!$v.nonFinanceInvoiceInfo.income_tax_withholding.between" class="form-group__message text-danger">{{ $t('validation.enter_valid_tax_rate') }}</span>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $tc('payments.payment_type', 1) }} </label>
                <base-input
                  :invalid="$v.nonFinanceInvoiceInfo.payment_type.$error"
                  v-model="nonFinanceInvoiceInfo.payment_type"
                  type="text"
                  @input="$v.nonFinanceInvoiceInfo.payment_type.$touch()"
                />
                <div v-if="$v.nonFinanceInvoiceInfo.payment_type.$error">
                  <span v-if="!$v.nonFinanceInvoiceInfo.payment_type.alphaNum" class="text-danger">
                    {{ $tc('validation.alphaNum') }}
                  </span>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="formData.request_level =='Eligible' && invoiceStatus=='AUTHORIZED'" class="payment-card card">
        <div class="card-body">
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.schedule_payment_date') }} <span class="text-danger"> *</span> </label>
                <base-date-picker
                  v-model="nonFinanceInvoiceInfo.schedule_payment_date"
                  :calendar-button="true"
                  calendar-button-icon="calendar"
                />
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.payment_code') }} </label>
                <div class="base-read-box base-read-box-field">
                  <span v-html="nonFinanceInvoiceInfo.payment_code"></span>
                </div>

              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $tc('payments.payment_type', 1) }} </label>
                <div class="base-read-box base-read-box-field">
                  <span v-html="nonFinanceInvoiceInfo.payment_type"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="payment-card card">
        <div class="card-body">
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.date') }}</label><span class="text-danger"> *</span>
                <base-date-picker
                  v-model="formData.payment_date"
                  :invalid="$v.formData.payment_date.$error"
                  :calendar-button="true"
                  calendar-button-icon="calendar"
                  @change="$v.formData.payment_date.$touch()"
                />
                <div v-if="$v.formData.payment_date.$error">
                  <span v-if="!$v.formData.payment_date.required" class="text-danger">{{ $t('validation.required') }}</span>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.reference_number') }}</label>
                <base-input
                  v-model="formData.referenceNumber"
                />
              </div>
            </div>
            <!-- <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.system_generated_payment_number') }}</label>
                <base-prefix-input
                  :invalid="$v.paymentNumAttribute.$error"
                  v-model.trim="paymentNumAttribute"
                  :prefix="paymentPrefix"
                  :readonly="true"
                  @input="$v.paymentNumAttribute.$touch()"
                />
                <div v-if="$v.paymentNumAttribute.$error">
                  <span v-if="!$v.paymentNumAttribute.required" class="text-danger">{{ $tc('validation.required') }}</span>
                  <span v-if="!$v.paymentNumAttribute.numeric" class="text-danger">{{ $tc('validation.numbers_only') }}</span>
                </div>
              </div>
            </div> -->
            <div class="col-sm-6" >
              <label class="form-label">{{ $t('payments.supplier') }}</label><span class="text-danger"> *</span>
              <base-select
                v-model="user"
                :options="userList"
                :searchable="true"
                :show-labels="false"
                :allow-empty="false"
                :disabled="isEdit"
                :placeholder="$t('suppliers.select_a_supplier')"
                label="name"
                track-by="id"
              />
            </div>
            <div class="col-sm-6" >
              <div class="form-group">
                <label class="form-label">{{ $t('payments.invoice') }}</label><span class="text-danger"> *</span>
                <base-select
                  v-model="invoice"
                  :options="invoiceList"
                  :invalid="$v.invoice.$error"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="false"
                  :disabled="isEdit"
                  :placeholder="$t('invoices.select_invoice')"
                  :custom-label="invoiceWithAmount"
                  track-by="id"
                />
                <div v-if="$v.invoice.$error">
                  <span v-if="!$v.invoice.required" class="text-danger">{{ $tc('validation.required') }}</span>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.amount') }}</label><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :class="{'invalid' : $v.formData.amount.$error}"
                    v-model="amount"
                    v-bind="paymentCurrency"
                    class="input-field"
                  />
                </div>
                <div v-if="$v.formData.amount.$error">
                  <span v-if="!$v.formData.amount.required" class="text-danger">{{ $t('validation.required') }}</span>
                  <span v-if="!$v.formData.amount.between && $v.formData.amount.numeric && formData.amount <= 0" class="text-danger">{{ $t('validation.payment_greater_than_zero') }}</span>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.payment_mode') }}</label>
                <base-select
                  v-model="formData.payment_method"
                  :options="paymentModes"
                  :searchable="true"
                  :show-labels="false"
                  :placeholder="$t('payments.select_payment_mode')"
                  label="name"
                >
                  <div slot="afterList">
                    <button type="button" class="list-add-button" @click="addPaymentMode">
                      <font-awesome-icon class="icon" icon="cart-plus" />
                      <label>{{ $t('settings.customization.payments.add_payment_mode') }}</label>
                    </button>
                  </div>
                </base-select>
              </div>
            </div>

            <div class="col-sm-12 ">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.note') }}</label>
                <base-text-area
                  v-model="formData.notes"
                  @input="$v.formData.notes.$touch()"
                />
                <div v-if="$v.formData.notes.$error">
                  <span v-if="!$v.formData.notes.maxLength" class="text-danger">{{ $t('validation.notes_maxlength') }}</span>
                </div>
              </div>
            </div>
            <div class="col-sm-12">
              <div class="form-group collapse-button-container">
                <base-button
                  :loading="isLoading"
                  icon="save"
                  color="theme"
                  type="submit"
                  class="collapse-button"
                >
                  {{ $t('payments.save_payment') }}
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MultiSelect from 'vue-multiselect'
import { validationMixin } from 'vuelidate'
import moment from 'moment'
import SingleInvoiceRow from '../invoices/SingleInvoiceRow'
const { required, between, maxLength, numeric, alphaNum } = require('vuelidate/lib/validators')
export default {
  components: { MultiSelect, SingleInvoiceRow},
  mixins: [validationMixin],
  data () {
    return {
      fetchData: [],
      nonFinanceInvoiceInfo: {
        schedule_payment_date: null,
        income_tax_withholding: 0,
        // sales_tax_withholding: 0,
        payment_type: null,
        payment_code: null
      },
      formData: {
        customer_id: null,
        user_id: null,
        payment_number: null,
        payment_date: null,
        amount: 0,
        payment_method: null,
        invoice_id: null,
        notes: null,
        payment_method_id: null,
        user: null,
        request_level: null,
        referenceNumber: null
      },
      money: {
        decimal: '.',
        thousands: ',',
        prefix: 'Rs ',
        precision: 2,
        masked: false
      },
      customerCurrency: null,
      userCurrency: null,
      customer: null,
      user: null,
      invoice: null,
      customerList: [],
      userList: [],
      invoiceList: [],
      isLoading: false,
      maxPayableAmount: Number.MAX_SAFE_INTEGER,
      isSettingInitialData: true,
      paymentNumAttribute: null,
      paymentPrefix: '',
      wants_to_pay: false,
      invoiceRow: false,
      userType: null,

      invoiceFunder: {},
      invoiceSupplier: {},
      setInvoice: 0,
      receiverDisable: false,

      userShow: false,
      customerShow: false,
      defaultInputTax: {
        decimal: '.',
        thousands: ',',
        prefix: '% ',
        precision: 2,
        masked: false
      },
      invoiceStatus: null,
      originalInvoiceAmount: 0,
      GST: 0

    }
  },
  validations () {
    return {
      nonFinanceInvoiceInfo: {
        income_tax_withholding: {
          between: between(0, 99)
        },
        // sales_tax_withholding:{
        //   between: between(0, 99)
        // },
        payment_type: {
          alphaNum
        },
        payment_code: {
          numeric
        }
      },
      invoice: {
        required
      },
      formData: {
        payment_date: {
          required
        },
        amount: {
          required,
          between: between(1, this.maxPayableAmount + 1)
        },
        notes: {
          maxLength: maxLength(255)
        }
      }
      // paymentNumAttribute: {
      //   required,
      //   numeric
      // }
    }
  },
  computed: {
    ...mapGetters('currency', [
      'defaultCurrencyForInput'
    ]),
    ...mapGetters('payment', [
      'paymentModes'
    ]),
    // sales_tax_withholding:{
    //   get: function () {
    //     if(this.originalInvoiceAmount && this.nonFinanceInvoiceInfo.income_tax_withholding>0){
    //       this.formData.amount = Math.floor(this.originalInvoiceAmount-((this.nonFinanceInvoiceInfo.income_tax_withholding/100)*this.originalInvoiceAmount) - ((this.nonFinanceInvoiceInfo.sales_tax_withholding/100)*this.GST))
    //     }else{
    //       this.formData.amount = Math.floor(this.originalInvoiceAmount-(this.nonFinanceInvoiceInfo.sales_tax_withholding/100)*this.GST)
    //     }

    //     return this.nonFinanceInvoiceInfo.sales_tax_withholding
    //   },
    //   set: function (newValue) {
    //     this.nonFinanceInvoiceInfo.sales_tax_withholding = newValue
    //   }
    // },
    income_tax_withholding: {
      get: function () {
        return this.nonFinanceInvoiceInfo.income_tax_withholding
      },
      set: function (newValue) {
        this.nonFinanceInvoiceInfo.income_tax_withholding = newValue
        this.updateAmount()
      }
    },
amount: {
  get: function () {
    const truncated = Math.floor(this.formData.amount * 100) / 100;
    const result = truncated.toFixed(2);
    // console.log('Getter - this.formData.amount:', this.formData.amount, '→ Returned:', result);
    return result;
  },
  set: function (newValue) {
  // console.log('Setter - newValue received:', newValue);
  let parsed = parseFloat(newValue.toString().replace(/,/g, ''));
  this.formData.amount = (Math.trunc(parsed * 100) / 100).toFixed(2);
  // console.log('Setter - this.formData.amount updated to:', this.formData.amount);
}

},


    isEdit () {
      if (this.$route.name === 'payments.edit') {
        return true
      }
      return false
    },
    paymentCurrency () {
      if (this.customer && this.customerCurrency) {
        return {
          decimal: this.customerCurrency.decimal_separator,
          thousands: this.customerCurrency.thousand_separator,
          prefix: this.customerCurrency.symbol + ' ',
          precision: this.customerCurrency.precision,
          masked: false
        }
      } else if (this.user && this.userCurrency) {
        return {
          decimal: this.userCurrency.decimal_separator,
          thousands: this.userCurrency.thousand_separator,
          prefix: this.userCurrency.symbol + ' ',
          precision: this.userCurrency.precision,
          masked: false
        }
      } else {
        return this.defaultCurrencyForInput
      }
    }
  },
  watch: {
    user (newValue) {
      this.formData.user_id = newValue.id
      this.userCurrency = newValue.account.currency
      this.wants_to_pay = false
      if (!this.isEdit) {
        if (this.isSettingInitialData) {
          this.isSettingInitialData = false
        } else {
          this.invoice = null
          this.formData.invoice_id = null
        }

        // this.formData.amount = 0
        this.invoiceList = []
        this.fetchUserInvoices(newValue.id)
        this.setInvoice = this.setInvoice + 1
      }
    },
    invoice (newValue) {
      if (newValue) {
        this.formData.invoice_id = newValue.id
        if (!this.isEdit) {
          this.setPaymentAmountByInvoiceData(newValue.id)
        }
      }
    }
  },
  created () {
    if (this.$route.name === 'anchor.invoice.payments.create') {
      this.invoiceRow = true
      this.loadInvoice()
      this.setInvoice = 1
    }
    if (this.$route.name === 'anchor.invoice.payments.create') {
      this.wants_to_pay = true
    }
    if (localStorage.getItem('account_type') == 'anchor') {
      this.userType = 'anchor'
      this.userShow = true
    } else if (localStorage.getItem('account_type') == 'supplier') {
      this.userType = 'supplier'
      this.customerShow = true
    } else if (localStorage.getItem('account_type') == 'funder') {
      this.userType = 'funder'
    }
  },
  async mounted () {
    this.$nextTick(() => {
      this.loadData()
      if (this.$route.params.id && !this.isEdit) {
        this.setInvoicePaymentData()
      }
    })
  },
  methods: {
    ...mapActions('invoice', [
      'fetchInvoice',
      'SingleInvoiceArray',
      'fetchInvoiceShow',
      'addNonFinanceInvoiceInfo',
      'markAsAuthorized',
      'unpaidCustomerInvoice',
      'unpaidUserInvoice'
    ]),
    ...mapActions('payment', [
      'fetchCreatePayment',
      'addPayment',
      'updatePayment',
      'fetchEditPaymentData',
      'anchorAddPayment'
    ]),
    ...mapActions('modal', [
      'openModal'
    ]),
    ...mapActions('companyImpactOthers', [
      'addCompanyImpactOthers',
      'fetchCompanyImpactAccount',
      'getImpact'
    ]),
    async loadInvoice () {
      let response = await this.SingleInvoiceArray(this.$route.params.id)
      if (response.data) {
        this.fetchData = response.data.invoice
      }
    },
    invoiceWithAmount ({ reference_number, due_amount }) {
      if (this.userCurrency != null) {
        return `${reference_number} (${this.$utils.formatGraphMoney(due_amount, this.userCurrency)})`
      } else {
        return `${reference_number} (${this.$utils.formatGraphMoney(due_amount, this.defaultCurrencyForInput)})`
      }
    },

    updateAmount () {
      const withholdingPercentage = this.nonFinanceInvoiceInfo.income_tax_withholding / 100
      const originalAmount = this.originalInvoiceAmount
      if (originalAmount) {
        this.formData.amount =
          (originalAmount - (withholdingPercentage * originalAmount * (this.GST || 1)))

      } else {
        this.formData.amount =
          originalAmount - (withholdingPercentage * originalAmount)

      }
      this.formData.amount = this.formData.amount
    },
    async addPaymentMode () {
      this.openModal({
        'title': 'Add Payment Mode',
        'componentName': 'PaymentMode'
      })
    },
    async loadData () {
      if (this.isEdit) {
        let response = await this.fetchEditPaymentData(this.$route.params.id)
        this.customerList = response.data.customers
        this.formData = { ...response.data.payment }
        this.customer = response.data.payment.customer
        this.formData.payment_date = moment(response.data.payment.payment_date, 'YYYY-MM-DD').toString()
        this.formData.amount = (Math.floor(parseFloat(response.data.payment.amount.replace(/,/g, '')) * 100) / 100).toFixed(2)
        this.paymentPrefix = response.data.payment_prefix
        this.paymentNumAttribute = response.data.nextPaymentNumber
        this.formData.payment_method = response.data.payment.payment_method
        if (response.data.payment.invoice !== null) {
          this.maxPayableAmount = parseInt(response.data.payment.amount) + parseInt(response.data.payment.invoice.due_amount)
          this.invoice = response.data.payment.invoice
        }
      } else {
        let response = await this.fetchCreatePayment()
        this.customerList = response.data.customers
        this.userList = response.data.suppliers
        this.paymentNumAttribute = response.data.nextPaymentNumberAttribute
        this.paymentPrefix = response.data.payment_prefix
        this.formData.payment_date = moment(new Date()).toString()
      }
      return true
    },
    async setInvoicePaymentData () {
      let data = await this.fetchInvoiceShow(this.$route.params.id)
      if (this.setInvoice > 3 || this.setInvoice === 1) {
        this.customer = data.data.invoice.customer
        this.user = data.data.invoice.user
      }

      this.invoice = data.data.invoice

      if ((this.$route.name === 'anchor.invoice.payments.create') && data.data.invoice.funder_invoices) {
        this.invoiceFunder = data.data.invoice.funder_invoices.funder
        this.invoiceSupplier = data.data.invoice.user.company.admin
        this.wants_to_pay = true
        if (data.data.invoice.payment_receiver_id) {
          this.formData.user = data.data.invoice.payment_receiver_id
          this.receiverDisable = true
        } else {
          this.formData.user = data.data.invoice.user.id
          this.receiverDisable = false
        }
      }
    },
    async saveNonFinanceInvoiceInfo () {
      this.$v.nonFinanceInvoiceInfo.$touch()
      let data = {
        ...this.nonFinanceInvoiceInfo,
        invoice_id: this.formData.invoice_id,
        schedule_payment_date: moment(this.formData.schedule_payment_date).format('DD/MM/YYYY')
      }
      let response = await this.addNonFinanceInvoiceInfo(data)
      if (response.data.success == true) {
        let res = await this.markAsAuthorized({ invoice_id: this.formData.invoice_id})
        if (res.data) {
          this.invoiceStatus = res.data.invoice.status
          window.toastr['success'](this.$tc('invoices.mark_as_authorized_successfully'))
        }
      }
    },
    filter_taxes (tax) {
      return tax.name == 'GST'
    },
    async setPaymentAmountByInvoiceData (id) {
      let invoiceAmountData = await this.fetchInvoiceShow(id)
      if (invoiceAmountData.data.invoice.taxes.length > 0) {
        let GST_TAX = invoiceAmountData.data.invoice.taxes.filter(this.filter_taxes)
        if (GST_TAX.length > 0) {
          this.GST = GST_TAX[0].amount
        }
      }

      this.originalInvoiceAmount = invoiceAmountData.data.invoice.due_amount
      this.formData.amount = parseFloat(invoiceAmountData.data.invoice.due_amount);
      this.maxPayableAmount = invoiceAmountData.data.invoice.due_amount
      this.formData.request_level = invoiceAmountData.data.invoice.request_level
      this.invoiceStatus = invoiceAmountData.data.invoice.status
      this.customer = invoiceAmountData.data.invoice.customer
      if (this.formData.request_level == 'Eligible' && this.invoiceStatus == 'SENT') {
        this.nonFinanceInvoiceInfo.schedule_payment_date = moment(new Date()).toString()

        this.getCompanyImpactOnAccount()
      }

      if (this.formData.request_level == 'Eligible' && this.invoiceStatus == 'AUTHORIZED' && invoiceAmountData.data.invoice.non_fianance_invoice_info) {
        this.nonFinanceInvoiceInfo.schedule_payment_date = invoiceAmountData.data.invoice.non_fianance_invoice_info.schedule_payment_date
        this.nonFinanceInvoiceInfo.payment_type = invoiceAmountData.data.invoice.non_fianance_invoice_info.payment_type
        this.nonFinanceInvoiceInfo.payment_code = invoiceAmountData.data.invoice.non_fianance_invoice_info.payment_code
      }

      if ((this.$route.name === 'anchor.payments.create' || this.$route.name === 'anchor.invoice.payments.create') && invoiceAmountData.data.invoice.funder_invoices) {
        this.invoiceFunder = invoiceAmountData.data.invoice.funder_invoices.funder
        this.invoiceSupplier = invoiceAmountData.data.invoice.user
        this.formData.user = invoiceAmountData.data.invoice.user.id
        this.wants_to_pay = true
        if (invoiceAmountData.data.invoice.payment_receiver_id) {
          this.formData.user = invoiceAmountData.data.invoice.payment_receiver_id
          this.receiverDisable = true
        } else {
          this.formData.user = invoiceAmountData.data.invoice.user.id
          this.receiverDisable = false
        }
      }
    },
    async fetchUserInvoices (userID) {
      let data = { user_id: userID}
      try {
        let response = await this.unpaidUserInvoice(data)
        if (response.data.result == 'success') {
          this.invoiceList = response.data.invoices
        } else if (response.data.result == 'error') {
          window.toastr['error'](response.data.message)
        }
      } catch (error) {
        this.isLoading = false
        window.toastr['error'](error.response.data.message)
      }
      if (this.setInvoice == 3) {
        this.setInvoicePaymentData()
      }
    },
    async getCompanyImpactOnAccount () {
      let data = {
        company_id: localStorage.getItem('selectedCompany'),
        guest_company_id: this.user.company_id
      }

    //   let response = await this.getImpact(data)
    //   if (response.data && response.data.sales_tax_withholding) {
    //     this.nonFinanceInvoiceInfo.sales_tax_withholding= response.data.sales_tax_withholding
    //   }
    },

    async submitPaymentData () {
      this.$v.invoice.$touch()
      this.$v.formData.$touch()
      if (this.formData.request_level == 'Eligible' && this.invoiceStatus == 'SENT') {
        this.$v.nonFinanceInvoiceInfo.$touch()
      }
      if (this.$v.$invalid) {
        return true
      }

      this.formData.payment_number = this.paymentPrefix + '-' + this.paymentNumAttribute

      if (this.isEdit) {
        let data = {
          editData: {
            ...this.formData,
            payment_method_id: this.formData.payment_method ? this.formData.payment_method.id : null,
            payment_date: moment(this.formData.payment_date).format('DD/MM/YYYY')
          },
          id: this.$route.params.id
        }
        try {
          let response = await this.updatePayment(data)
          if (response.data.success) {
            window.toastr['success'](this.$t('payments.updated_message'))
            this.$router.push('/anchor/payments')
            return true
          }
          if (response.data.error === 'invalid_amount') {
            window.toastr['error'](this.$t('invalid_amount_message'))
            return false
          }
          window.toastr['error'](response.data.error)
        } catch (err) {
          this.isLoading = false
          if (err.response.data.errors.payment_number) {
            window.toastr['error'](err.response.data.errors.payment_number)
            return true
          }
          window.toastr['error'](err.response.data.message)
        }
      } else {
        let data = {
          ...this.formData,
          payment_method_id: this.formData.payment_method ? this.formData.payment_method.id : null,
          payment_date: moment(this.formData.payment_date).format('DD/MM/YYYY')
        }
        this.isLoading = true
        try {
          if (this.formData.request_level == 'Eligible' && this.invoiceStatus == 'SENT') {
            let nonFinancedata = {
              ...this.nonFinanceInvoiceInfo,
              invoice_id: this.formData.invoice_id,
              schedule_payment_date: moment(this.formData.schedule_payment_date).format('DD/MM/YYYY')
            }
            let nonFinanceDataresponse = await this.addNonFinanceInvoiceInfo(nonFinancedata)
            if (nonFinanceDataresponse.data.success == true) {
              let authorizedResponse = await this.markAsAuthorized({ invoice_id: this.formData.invoice_id})
              if (authorizedResponse.data) {
                this.invoiceStatus = authorizedResponse.data.invoice.status
                window.toastr['success'](this.$t('invoices.mark_as_authorized_successfully'))
              }
            }
          }

          let response = await this.anchorAddPayment(data)
          if (response.data.success) {
            window.toastr['success'](this.$t('payments.created_message'))
            if (response.data.message) {
              window.toastr['success'](response.data.message)
            }
            this.$router.push('/anchor/payments')

            this.isLoading = true
            return true
          }
          if (response.data.error === 'invalid_amount') {
            window.toastr['error'](this.$t('invalid_amount_message'))
            return false
          }
          window.toastr['error'](response.data.error)
        } catch (err) {
          this.isLoading = false
          if (err.response.data.errors.payment_number) {
            window.toastr['error'](err.response.data.errors.payment_number)
            return true
          }
          window.toastr['error'](err.response.data.message)
        }
      }
    }
  }
}
</script>
<style scoped>
.table-component{
  min-height: 0px;
}
</style>
